import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * Detects structural type changes and content changes in file editors
 * 
 * @param {string} currentValue - Current editor content
 * @param {string} initialValue - Original loaded content
 * @param {string} fileType - File type: 'tools', 'policy', 'prompt'
 * @returns {Object} { typeChanged, contentChanged, changeInfo }
 */
export const useFileChangeDetection = (currentValue, initialValue, fileType) => {
  const [originalType, setOriginalType] = useState(null);
  const [currentType, setCurrentType] = useState(null);
  const [capturedInitialValue, setCapturedInitialValue] = useState(null);

  // Detect type based on file structure
  const detectType = useCallback((content) => {
    if (!content) return null;

    try {
      const parsed = JSON.parse(content);

      if (fileType === "tools") {
        if (parsed.tools !== undefined) return "expert";
        if (parsed.agents !== undefined) return "swarm";
      }

      if (fileType === "policy") {
        return "standard";
      }

      return null;
    } catch {
      return null;
    }
  }, [fileType]);

  // Capture initialValue only once when it first has content
  useEffect(() => {
    if (initialValue && !capturedInitialValue) {
      setCapturedInitialValue(initialValue);
      const detected = detectType(initialValue);
      setOriginalType(detected);
      setCurrentType(detected);
    }
  }, [initialValue, capturedInitialValue, detectType]);

  // Track current type as content changes
  useEffect(() => {
    if (currentValue) {
      const detected = detectType(currentValue);
      setCurrentType(detected);
    }
  }, [currentValue, detectType]);

  // Determine change states - memoize to prevent infinite loops
  const typeChanged = originalType !== null && currentType !== null && originalType !== currentType;
  const contentChanged = initialValue !== currentValue;

  return useMemo(() => ({
    typeChanged,
    contentChanged,
    changeInfo: typeChanged ? {
      fromType: originalType,
      toType: currentType,
      fileType
    } : null
  }), [typeChanged, contentChanged, originalType, currentType, fileType]);
};
